import type { StaffScheduleBlock } from "../booking/scheduleBlocks.js";
import type { HourRange } from "./grid.js";

/** One active schedule block projected onto one London calendar day. */
export interface CalendarBlockSpan {
  blockId: string;
  providerId: number;
  day: string;
  reason: string;
  blockDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  startMinutes: number;
  endMinutes: number;
}

/**
 * The School bridge uses this fixed generic reason for its booking protection.
 * Hide that duplicate diary card only; keep the span for occupied-slot checks.
 * Other blocks still display their reason and offer their usual actions.
 */
export function isVisibleCalendarBlock(block: CalendarBlockSpan): boolean {
  return block.reason.trim() !== "School unavailable";
}

function clockMinutes(value: string | null): number | null {
  if (value === null) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

/**
 * Put a provider-scoped schedule block onto a particular diary day.
 *
 * Full-day ranges are inclusive at both ends because that is how the block
 * contract names a holiday. Partial-day blocks are single-day in the backend,
 * but checking the date range as well keeps this total if a malformed payload
 * ever reaches the browser.
 */
export function blockSpanOnDay(block: StaffScheduleBlock, day: string): CalendarBlockSpan | null {
  if (block.status !== "active" || block.removed_at !== null) return null;
  if (day < block.block_date || day > block.end_date) return null;

  let startMinutes = 0;
  let endMinutes = 24 * 60;
  if (block.start_time !== null || block.end_time !== null) {
    const start = clockMinutes(block.start_time);
    const end = clockMinutes(block.end_time);
    if (start === null || end === null || end <= start) return null;
    startMinutes = start;
    endMinutes = end;
  }

  return {
    blockId: block.id,
    providerId: block.provider_id,
    day,
    reason: block.reason,
    blockDate: block.block_date,
    endDate: block.end_date,
    startTime: block.start_time,
    endTime: block.end_time,
    startMinutes,
    endMinutes,
  };
}

export function blockSpansOnDay(blocks: readonly StaffScheduleBlock[], day: string): CalendarBlockSpan[] {
  return blocks
    .map((block) => blockSpanOnDay(block, day))
    .filter((span): span is CalendarBlockSpan => span !== null)
    .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);
}

/**
 * Week grids normally crop to lesson hours. A partial-day block is itself a
 * calendar event worth seeing, so widen that crop when it falls before or after
 * every lesson. Full-day blocks deliberately do not widen to midnight–midnight:
 * they fill whatever useful daytime range the week already shows instead of
 * turning a normal week into a 24-hour-tall grid.
 */
export function expandHourRangeForBlocks(
  range: HourRange,
  blocks: readonly CalendarBlockSpan[],
): HourRange {
  let startHour = range.startHour;
  let endHour = range.endHour;

  for (const block of blocks) {
    if (block.startTime === null) continue;
    startHour = Math.min(startHour, Math.floor(block.startMinutes / 60));
    endHour = Math.max(endHour, Math.ceil(block.endMinutes / 60));
  }

  return {
    startHour: Math.max(0, startHour),
    endHour: Math.min(24, endHour),
  };
}

export function describeCalendarBlock(block: CalendarBlockSpan): string {
  const dates = block.blockDate === block.endDate ? block.blockDate : `${block.blockDate} to ${block.endDate}`;
  if (block.startTime === null) return `${dates} · all day`;
  return `${dates} · ${block.startTime.slice(0, 5)}–${block.endTime?.slice(0, 5) ?? ""}`;
}
